import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import { orgsApi, type BootstrapInviteResult, type OrgUserRow } from '../features/orgs/api.ts';
import { Icon } from '../components/Icon.tsx';
import { statusLabel } from '../lib/status.ts';

function RoleBadge({ role }: { role: OrgUserRow['role'] }): JSX.Element {
  const label = role === 'SYS_ADMIN' ? 'System admin' : role === 'ORG_ADMIN' ? 'Admin' : 'Member';
  const cls = role === 'USER'
    ? 'bg-muted text-muted-foreground'
    : 'bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))]';
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{label}</span>;
}

export function AdminOrgDetail(): JSX.Element {
  const { id } = useParams({ from: '/admin/orgs/$id' });
  const qc = useQueryClient();
  const { data: org } = useQuery({ queryKey: ['admin', 'orgs', 'detail', id], queryFn: () => orgsApi.get(id) });
  const { data: users } = useQuery({ queryKey: ['admin', 'orgs', 'users', id], queryFn: () => orgsApi.listUsers(id) });

  const [email, setEmail] = useState('');
  const [result, setResult] = useState<BootstrapInviteResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resetDone, setResetDone] = useState<Record<string, boolean>>({});

  const invalidateUsers = () => { void qc.invalidateQueries({ queryKey: ['admin', 'orgs', 'users', id] }); };

  const invite = useMutation({
    mutationFn: () => orgsApi.bootstrapOrgAdmin(id, email),
    onSuccess: (r) => { setResult(r); setError(null); setEmail(''); },
    onError: () => setError('Failed to create invite. Check the email address.'),
  });

  const update = useMutation({
    mutationFn: ({ userId, patch }: { userId: string; patch: { role?: 'ORG_ADMIN' | 'USER'; isActive?: boolean } }) =>
      orgsApi.updateUser(id, userId, patch),
    onSuccess: invalidateUsers,
  });

  const resetPassword = useMutation({
    mutationFn: (userId: string) => orgsApi.resetUserPassword(id, userId),
    onSuccess: (_d, userId) => setResetDone((s) => ({ ...s, [userId]: true })),
  });

  return (
    <div className="page space-y-6">
      <Link to="/admin/orgs" className="inline-flex items-center gap-1 text-sm text-primary hover:underline"><Icon name="arrow_back" size={14} /> All orgs</Link>
      {org ? (
        <>
          <header>
            <h1 className="font-sans font-normal text-[2.25rem] md:text-[2.75rem] leading-tight break-words">{org.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {org.contactEmail} · <span className={`pill ${org.status === 'ACTIVE' ? 'pill-active' : 'pill-danger'}`}>{statusLabel(org.status)}</span> · {org._count.users} user(s)
            </p>
          </header>

          <section className="space-y-3">
            <h2 className="status-label text-muted-foreground">Users</h2>
            {(update.isError || resetPassword.isError) && (
              <p className="text-sm text-destructive">Something went wrong — the change was not applied.</p>
            )}
            <div className="card-paper overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left">
                  <tr className="text-muted-foreground">
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium hidden sm:table-cell">Email</th>
                    <th className="px-3 py-2 font-medium">Role</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users?.map((u) => {
                    const isSysAdmin = u.role === 'SYS_ADMIN';
                    const busy = update.isPending && update.variables?.userId === u.id;
                    return (
                      <tr key={u.id} className="border-t border-border align-top">
                        <td className="px-3 py-2.5">
                          <div className="font-medium text-foreground">{u.name}</div>
                          <div className="text-xs text-muted-foreground sm:hidden">{u.email}</div>
                        </td>
                        <td className="px-3 py-2.5 text-muted-foreground hidden sm:table-cell">{u.email}</td>
                        <td className="px-3 py-2.5"><RoleBadge role={u.role} /></td>
                        <td className="px-3 py-2.5">
                          <span className={`pill ${u.isActive ? 'pill-success' : 'pill-neutral'}`}>
                            {u.isActive ? 'Active' : 'Disabled'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          {isSysAdmin ? (
                            <span className="block text-right text-xs text-muted-foreground">—</span>
                          ) : (
                            <div className="flex flex-wrap justify-end gap-2">
                              {u.role === 'USER' ? (
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => { if (confirm(`Make ${u.name} an admin?`)) update.mutate({ userId: u.id, patch: { role: 'ORG_ADMIN' } }); }}
                                  className="btn-secondary btn-sm"
                                >
                                  Make admin
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => { if (confirm(`Remove admin rights from ${u.name}?`)) update.mutate({ userId: u.id, patch: { role: 'USER' } }); }}
                                  className="btn-secondary btn-sm"
                                >
                                  Remove admin
                                </button>
                              )}
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => update.mutate({ userId: u.id, patch: { isActive: !u.isActive } })}
                                className="btn-secondary btn-sm"
                              >
                                {u.isActive ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                type="button"
                                disabled={resetPassword.isPending}
                                onClick={() => { if (confirm(`Send a password reset email to ${u.email}?`)) resetPassword.mutate(u.id); }}
                                className="btn-secondary btn-sm"
                              >
                                Reset password
                              </button>
                              {resetDone[u.id] && (
                                <span className="pill pill-success">
                                  <Icon name="mark_email_read" size={13} /> Sent
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {users && users.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No users yet.</td></tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </section>

          <section className="card-paper p-4 space-y-3">
            <h2 className="status-label text-muted-foreground">Invite the first ORG_ADMIN</h2>
            <p className="text-sm text-muted-foreground">
              Send the resulting link to the person who&apos;ll own this organization. They&apos;ll pick a password and become ORG_ADMIN.
            </p>
            <form
              className="flex flex-col gap-2 sm:flex-row"
              onSubmit={(e) => { e.preventDefault(); if (email.trim()) invite.mutate(); }}
            >
              <input
                aria-label="ORG_ADMIN email"
                type="email"
                placeholder="org-admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input flex-1"
              />
              <button
                type="submit"
                disabled={invite.isPending || !email.trim()}
                className="btn-primary"
              >
                {invite.isPending ? 'Creating…' : 'Create invite'}
              </button>
            </form>
            {error && <p className="text-sm text-destructive">{error}</p>}
            {result && (
              <div className="card-paper bg-muted/30 p-3 flex items-start gap-3">
                <Icon name="mark_email_read" size={18} className="mt-0.5 text-primary shrink-0" />
                <p className="text-xs text-muted-foreground">
                  Invite emailed to <span className="font-medium text-foreground">{result.invite.email}</span>.
                  Link expires {new Date(result.invite.expiresAt).toLocaleString()}.
                </p>
              </div>
            )}
          </section>
        </>
      ) : (
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">Loading…</p>
      )}
    </div>
  );
}
